// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import React from "react";
import {
	CreateFunction,
	onCreateSubmit,
	onUpdateSubmit,
	UpdateFunction,
	useApplyCreate,
	useApplyUpdate,
} from "@hex-insights/app-modules";
import { DistributiveOmit } from "@hex-insights/core";
import { FormState, FormSubmitFunction, useOnSuccess } from "@hex-insights/forms";
import {
	StudentHomeRoomSectionEnrollment,
	StudentHomeRoomSectionEnrollmentCreateMutation,
	StudentHomeRoomSectionEnrollmentUpdateMutation,
} from "../../../Utilities";
import {
	StudentHomeRoomSectionEnrollmentFormConversion,
	StudentHomeRoomSectionEnrollmentFormState,
	StudentHomeRoomSectionEnrollmentFormValues,
	StudentHomeRoomSectionEnrollmentMutation,
} from "../../FormUtilities";

export type UseCreateFormArgs<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Create = keyof StudentHomeRoomSectionEnrollmentFormValues.Create,
> = UseCreateFormControlArgs<K> &
	Omit<
		UseCreateFormSubmissionArgs<
			StudentHomeRoomSectionEnrollmentCreateMutation["createStudentHomeRoomSectionEnrollment"],
			K
		>,
		"formState" | "create"
	>;

export function useCreateForm<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Create = keyof StudentHomeRoomSectionEnrollmentFormValues.Create,
>({ onSuccess, ...formStateArgs }: UseCreateFormArgs<K>) {
	const { formState, create } = useCreateFormControl(formStateArgs);
	const { onSubmit } = useCreateFormSubmission({ formState, create, onSuccess });

	return { formState, onSubmit };
}

export type UseCreateFormControlArgs<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Create = keyof StudentHomeRoomSectionEnrollmentFormValues.Create,
> = StudentHomeRoomSectionEnrollmentFormState.UseCreateFormStateArgs<K>;

export function useCreateFormControl<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Create = keyof StudentHomeRoomSectionEnrollmentFormValues.Create,
>(formStateArgs?: UseCreateFormControlArgs<K>) {
	const formState = StudentHomeRoomSectionEnrollmentFormState.useCreateFormState(formStateArgs);
	const create = StudentHomeRoomSectionEnrollmentMutation.useCreate<K>();

	return { formState, create };
}

export type UseCreateFormSubmissionArgs<
	T,
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Create = keyof StudentHomeRoomSectionEnrollmentFormValues.Create,
> = {
	formState: FormState<StudentHomeRoomSectionEnrollmentFormValues.CreateSubset<K>>;
	create: CreateFunction<StudentHomeRoomSectionEnrollmentFormValues.CreateSubset<K>, T>;
	onSuccess: (formState: FormState<StudentHomeRoomSectionEnrollmentFormValues.CreateSubset<K>>, data: T) => void;
};

export function useCreateFormSubmission<
	T,
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Create = keyof StudentHomeRoomSectionEnrollmentFormValues.Create,
>({ formState, create, onSuccess }: UseCreateFormSubmissionArgs<T, K>) {
	const { applyCreate, resultRef } = useApplyCreate(create);
	const onSubmit = React.useCallback<FormSubmitFunction<StudentHomeRoomSectionEnrollmentFormValues.CreateSubset<K>>>(
		async ({ formValues }) => onCreateSubmit(formValues, applyCreate),
		[applyCreate],
	);
	useOnSuccess(formState, (formState) => onSuccess(formState, resultRef.current!));

	return { onSubmit };
}

export type UseDetailFormArgs<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Detail = keyof StudentHomeRoomSectionEnrollmentFormValues.Detail,
> = UseDetailFormControlArgs<K> &
	Omit<
		UseDetailFormSubmissionArgs<
			StudentHomeRoomSectionEnrollmentUpdateMutation["updateStudentHomeRoomSectionEnrollment"],
			K
		>,
		"formState" | "update"
	>;

export function useDetailForm<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Detail = keyof StudentHomeRoomSectionEnrollmentFormValues.Detail,
>({ onSuccess, ...formStateArgs }: UseDetailFormArgs<K>) {
	const { formState, update } = useDetailFormControl(formStateArgs);
	const { onSubmit } = useDetailFormSubmission({ formState, update, onSuccess });

	return { formState, onSubmit };
}

export type UseDetailFormControlArgs<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Detail = keyof StudentHomeRoomSectionEnrollmentFormValues.Detail,
> = DistributiveOmit<StudentHomeRoomSectionEnrollmentFormState.UseDetailFormStateArgs<K>, "initialFormValues"> &
	Omit<StudentHomeRoomSectionEnrollmentFormConversion.ToFormValuesArgs<K>, "studentHomeRoomSectionEnrollment"> & {
		studentHomeRoomSectionEnrollment: StudentHomeRoomSectionEnrollmentFormConversion.ToFormValuesArgs<K>["studentHomeRoomSectionEnrollment"] & {
			id: StudentHomeRoomSectionEnrollment["id"];
		};
	};

export function useDetailFormControl<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Detail = keyof StudentHomeRoomSectionEnrollmentFormValues.Detail,
>({ studentHomeRoomSectionEnrollment, fields, ...formStateArgs }: UseDetailFormControlArgs<K>) {
	const formState = StudentHomeRoomSectionEnrollmentFormState.useDetailFormState({
		...formStateArgs,
		initialFormValues: () =>
			StudentHomeRoomSectionEnrollmentFormConversion.toFormValues({ studentHomeRoomSectionEnrollment, fields }),
	});
	const update = StudentHomeRoomSectionEnrollmentMutation.useUpdate<K>(studentHomeRoomSectionEnrollment.id);

	return { formState, update };
}

export type UseDetailFormSubmissionArgs<
	T,
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Detail = keyof StudentHomeRoomSectionEnrollmentFormValues.Detail,
> = {
	formState: FormState<StudentHomeRoomSectionEnrollmentFormValues.DetailSubset<K>>;
	update: UpdateFunction<StudentHomeRoomSectionEnrollmentFormValues.DetailSubset<K>, T>;
	onSuccess: (formState: FormState<StudentHomeRoomSectionEnrollmentFormValues.DetailSubset<K>>, data: T) => void;
};

export function useDetailFormSubmission<
	T,
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Detail = keyof StudentHomeRoomSectionEnrollmentFormValues.Detail,
>({ formState, update, onSuccess }: UseDetailFormSubmissionArgs<T, K>) {
	const { applyUpdate, resultRef } = useApplyUpdate(update);
	const onSubmit = React.useCallback<FormSubmitFunction<StudentHomeRoomSectionEnrollmentFormValues.DetailSubset<K>>>(
		async (formState) => onUpdateSubmit(formState, applyUpdate),
		[applyUpdate],
	);
	useOnSuccess(formState, (formState) => onSuccess(formState, resultRef.current!));

	return { onSubmit };
}

export type UseReadOnlyFormArgs<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Detail = keyof StudentHomeRoomSectionEnrollmentFormValues.Detail,
> = DistributiveOmit<StudentHomeRoomSectionEnrollmentFormState.UseReadOnlyFormStateArgs<K>, "initialFormValues"> &
	StudentHomeRoomSectionEnrollmentFormConversion.ToFormValuesArgs<K>;

export function useReadOnlyForm<
	K extends keyof StudentHomeRoomSectionEnrollmentFormValues.Detail = keyof StudentHomeRoomSectionEnrollmentFormValues.Detail,
>({ studentHomeRoomSectionEnrollment, fields, ...formStateArgs }: UseReadOnlyFormArgs<K>) {
	const formState = StudentHomeRoomSectionEnrollmentFormState.useReadOnlyFormState({
		...formStateArgs,
		initialFormValues: () =>
			StudentHomeRoomSectionEnrollmentFormConversion.toFormValues({ studentHomeRoomSectionEnrollment, fields }),
	});

	return { formState };
}
